import m from 'mithril';
import '@awesome.me/webawesome/dist/components/color-picker/color-picker.js';
import '@awesome.me/webawesome/dist/components/icon/icon.js';
import '@awesome.me/webawesome/dist/components/input/input.js';
import '@awesome.me/webawesome/dist/components/textarea/textarea.js';
import type WaColorPicker from '@awesome.me/webawesome/dist/components/color-picker/color-picker.js';
import type WaInput from '@awesome.me/webawesome/dist/components/input/input.js';
import type WaTextarea from '@awesome.me/webawesome/dist/components/textarea/textarea.js';

import type { MithrilViewEvent } from '../types';

/**
 * Shared attrs for controlled form fields.
 */
export interface FieldAttrs extends m.Attributes {
  /** Field label. */
  label: string;
  /** Field name. */
  name: string;
  /** Current field value. */
  value: string;
  /** Optional Material icon name shown at the start of the field. */
  startIcon?: string;
  /** Placeholder text. */
  placeholder?: string;
  /** Validation hint or supporting text. */
  hint?: string;
  /** Browser input type. */
  type?: 'email' | 'password' | 'text';
  /** Whether the field is disabled. */
  disabled?: boolean;
  /** Whether the field is required. */
  required?: boolean;
  /** Field autocomplete value. */
  autocomplete?: string;
  /** Value change callback. */
  onInput: (value: string) => void;
}

/**
 * Renders a controlled Webawesome text input.
 */
export const TextField: m.Component<FieldAttrs> = {
  view: ({ attrs }) => {
    return m('wa-input', {
      class: 'field',
      label: attrs.label,
      name: attrs.name,
      type: attrs.type ?? 'text',
      value: attrs.value,
      placeholder: attrs.placeholder,
      hint: attrs.hint,
      disabled: attrs.disabled,
      required: attrs.required,
      autocomplete: attrs.autocomplete,
      appearance: 'filled-outlined',
      'password-toggle': attrs.type === 'password' ? true : undefined,
      oninput: (event: MithrilViewEvent<WaInput>) => {
        event.redraw = false;
        attrs.onInput(event.target.value ?? '');
      },
    }, attrs.startIcon
      ? m('wa-icon', { slot: 'start', library: 'material', name: attrs.startIcon })
      : null);
  },
};

/**
 * Renders a controlled Webawesome textarea.
 */
export const TextAreaField: m.Component<FieldAttrs> = {
  view: ({ attrs }) => {
    return m('wa-textarea', {
      class: 'field',
      label: attrs.label,
      name: attrs.name,
      value: attrs.value,
      placeholder: attrs.placeholder,
      hint: attrs.hint,
      required: attrs.required,
      appearance: 'filled-outlined',
      rows: 5,
      resize: 'vertical',
      oninput: (event: MithrilViewEvent<WaTextarea>) => {
        event.redraw = false;
        attrs.onInput(event.target.value ?? '');
      },
    });
  },
};

/**
 * Color picker attrs.
 */
export interface ColorFieldAttrs extends m.Attributes {
  /** Field label. */
  label: string;
  /** Current color value. */
  value: string;
  /** Supporting text shown below the picker. */
  hint?: string;
  /** Value change callback. */
  onChange: (value: string) => void;
}

/**
 * Renders a controlled Webawesome color picker.
 */
export const ColorField: m.Component<ColorFieldAttrs> = {
  view: ({ attrs }) => {
    return m('label', { class: 'color-field' }, [
      m('span', { class: 'color-field__label' }, attrs.label),
      m('wa-color-picker', {
        value: attrs.value,
        format: 'hex',
        swatches: '#2563eb;#1e40af;#0f766e;#166534;#9333ea;#c2410c;#be123c;#334155',
        onchange: (event: MithrilViewEvent<WaColorPicker>) => {
          event.redraw = false;
          attrs.onChange(event.target.value ?? '#2563eb');
        },
      }),
      attrs.hint ? m('span', { class: 'color-field__hint' }, attrs.hint) : null,
    ]);
  },
};
